import { useCallback } from "react";
import SegmentedControl from "src/components/SegmentedControl";
import { UseFormReturnType } from "@mantine/form";
import ActionCard from "src/components/ActionCard";
import { DiscountCodeType } from "src/generated/graphql";
import { IIDAndName } from "src/utils/helperTypes";
import {
  ActionIcon,
  Box,
  Flex,
  List,
  ScrollArea,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { AiFillMinusCircle } from "react-icons/ai";
import Switch from "src/components/Switch/Switch";
import { useModals } from "@mantine/modals";
import { SearchList } from "./SearchList";
import { IDiscountForm, TypeList } from "../Details";

interface DiscountTypeListProps {
  form: UseFormReturnType<IDiscountForm>;
  isActive?: boolean;
  list: IIDAndName[];
  onListUpdate: (
    statePartial:
      | Partial<TypeList>
      | ((currentState: TypeList) => Partial<TypeList>)
  ) => void;
}

const discountListName = {
  [DiscountCodeType.Agreement]: "agreements",
  [DiscountCodeType.Product]: "products",
};

export const DiscountTypeList = ({
  form,
  list = [],
  isActive = false,
  onListUpdate,
}: DiscountTypeListProps) => {
  const theme = useMantineTheme();
  const modals = useModals();

  const border = `1px solid ${theme.colors.secondary[2]}`;
  const { value: discountTypeValue, ...discountTypeProps } =
    form.getInputProps("discountCodeType");
  const { value: isRestricted, ...isRestrictedProps } =
    form.getInputProps("isRestricted");
  const { value: recurring, ...recurringProps } =
    form.getInputProps("recurring");

  const handleAddDiscountType = (nameId: string) => {
    const selectedItem = nameId.split("|");
    const discountType = discountTypeValue as DiscountCodeType;
    onListUpdate({
      [discountType]: [...list, { name: selectedItem[0], id: selectedItem[1] }],
    });

    const typeList: string[] = form.getInputProps(
      discountListName[discountType]
    ).value;

    form.setFieldValue(discountListName[discountType], [
      ...typeList,
      selectedItem[1],
    ]);
    modals.closeAll();
  };

  const handleCreateModal = useCallback(() => {
    modals.openModal({
      closeOnClickOutside: false,
      title: `Add ${discountTypeValue}`,
      children: (
        <SearchList
          type={discountTypeValue as DiscountCodeType}
          onCancel={modals.closeAll}
          onSubmit={handleAddDiscountType}
          currentIdList={list.map((item) => item.id)}
        />
      ),
    });
  }, [modals, discountTypeValue]);

  const handleFilterDiscountType = (id: string) => {
    const filterDiscountTypeList = list.filter((el) => el.id !== id);
    const discountType = discountTypeValue as DiscountCodeType;
    onListUpdate({
      [discountType]: filterDiscountTypeList,
    });

    const typeList: string[] = form.getInputProps(
      discountListName[discountType]
    ).value;
    const filterInputDiscountTypeList = typeList.filter(
      (typeId) => typeId !== id
    );

    form.setFieldValue(
      discountListName[discountType],
      filterInputDiscountTypeList
    );
    modals.closeAll();
  };

  const handleDeleteModal = (listElement: IIDAndName) => {
    modals.openContextModal("delete", {
      closeOnClickOutside: false,
      title: `Confirm ${listElement.name} Delete`,
      centered: true,
      innerProps: {
        onDelete: () => handleFilterDiscountType(listElement.id),
      },
    });
  };

  return (
    <ActionCard
      title="Type"
      mb={10}
      rightIconButton={
        <SegmentedControl
          data={Object.keys(DiscountCodeType)}
          value={discountTypeValue}
          {...discountTypeProps}
          disabled={isActive}
        />
      }
    >
      <Box mb={5}>
        {discountTypeValue === DiscountCodeType.Agreement && (
          <Switch
            mb={15}
            labelPosition="left"
            disabled={isActive}
            label={`Applies to recurring charges`}
            checked={recurring}
            {...recurringProps}
          />
        )}
        <Switch
          labelPosition="left"
          disabled={isActive}
          label={`Valid only on specific ${discountTypeValue}s`}
          checked={isRestricted}
          {...isRestrictedProps}
        />
      </Box>
      {isRestricted && (
        <ActionCard
          disabled={isActive}
          title={`${discountTypeValue}s`}
          handleModal={handleCreateModal}
          margin={"0px"}
        >
          {!!list.length && (
            <ScrollArea style={{ height: 97.5 }}>
              <List
                listStyleType="none"
                withPadding={false}
                icon={null}
                styles={{
                  item: {
                    borderBottom: border,
                    "&:last-child": {
                      borderBottom: "none",
                    },
                  },
                  itemWrapper: {
                    margin: "8px 15px",
                    display: "flex",
                    flexDirection: "row",
                    "& > span": {
                      display: "block",
                      width: "100%",
                    },
                  },
                }}
              >
                {list.map((el) => (
                  <List.Item key={el.id}>
                    <Flex justify="space-between" align="center">
                      <Text c={theme.colors.secondaryTextColor[6]}>
                        {el.name}
                      </Text>
                      <ActionIcon
                        color="red"
                        variant="transparent"
                        onClick={() => handleDeleteModal(el)}
                        disabled={isActive}
                      >
                        <AiFillMinusCircle size={24} />
                      </ActionIcon>
                    </Flex>
                  </List.Item>
                ))}
              </List>
            </ScrollArea>
          )}
        </ActionCard>
      )}
    </ActionCard>
  );
};
